import React from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Route, Routes } from 'react-router-dom';
import ErrorDialogModal from '@components/ErrorDialogModal';

import { ENTITY_TITLES, EventTypes, FORM_MODE, Views } from './constants';
import Home from './Home';
import DataTable from './components/dataTables/DataTable';
import IngestionFailuresTable from './tables/IngestionFailuresTable';
import AccessRequestTable from './components/AccessRequestAdmin/AccessRequestTable';
import UserAdmin from './components/UserAdmin/UserAdmin';
import MyProfile from './components/MyProfile';
import RoleAdmin from './components/RoleAdmin/RoleAdmin';
import RoadClosureLocationsAdmin from './components/RoadClosureLocationsAdmin/RoadClosureLocationsAdmin';
import RoadClosureAreasAdmin from './components/RoadClosureAreasAdmin/RoadClosureAreasAdmin';
import AvalancheAreasAdmin from './components/AvalancheAreasAdmin/AvalancheAreasAdmin';
import ManufacturerAdmin from './components/Manufacturer/ManufacturerAdmin';
import SensorTypeAdmin from './components/SensorTypeAdmin/SensorTypeAdmin';
import SoilTypesAdmin from './components/SoilTypesAdmin/SoilTypesAdmin';
import WeatherStationAdmin from './components/WeatherStationAdmin/WeatherStationAdmin';
import ObservationTypeAdmin from './components/ObservationTypeAdmin/ObservationTypeAdmin';
import SensorAdmin from './components/SensorAdmin/SensorAdmin';
import SensorModelAdmin from './components/SensorModelAdmin/SensorModelAdmin';
import Map from './components/maps/map';
import DashboardPage from './components/dashboards/Page';
import SingleStationGraph from '@graphs/SingleStation/Page';
import MultiStationGraph from '@graphs/MultiStation/Page';
import GraphPresets from '@graphs/Presets/Page';
import { AvalancheEventWrapper } from '@components/Avalanche/AvalancheEvent';
import AvalancheProcessedEvent, {
  AvalancheProcessedEventWrapper,
} from './components/Avalanche/AvalancheProcessedEvent';
import Notification from './components/Notifications/Notification';
import AvalancheDashboardPage from './components/AvalancheMonitor/Page';
import ErrorBoundary from './ErrorBoundary';
import ForecastRecipient from './components/ForecastRecipient/ForecastRecipient';
import ManualDataValidation from './components/ManualDataValidation';
import UserPreferences from './components/UserPreferences/UserPreferences';
import VariableMessageSignAdmin from './components/VariableMessageSigns/VmsAdmin';
import VMSControlAdmin from './components/VMSControl/VMSControlAdmin';
import FrostProbe from './components/Graphs/FrostProbe/Page';
import { AwpContainer } from '@components/Common/AwpContainer';
import StandardGraphAxis from './components/StandardGraphAxis/StandardGraphAxis';
import StandardGraphAxisEdit from '@components/StandardGraphAxis/StandardGraphAxisEdit';
import StandardGraphAxisView from '@components/StandardGraphAxis/StandardGraphAxisView';
import StandardGraphAxisCreate from '@components/StandardGraphAxis/StandardGraphAxisCreate';
import AvalanchePrograms from '@components/AvalanchePrograms/AvalanchePrograms';
import AvalancheProgramView from '@components/AvalanchePrograms/AvalancheProgramView';
import AvalancheProgramCreate from '@components/AvalanchePrograms/AvalancheProgramCreate';
import AvalancheProgramEdit from '@components/AvalanchePrograms/AvalancheProgramEdit';
import HazardForecastAreas from '@components/HazardForecastArea/HazardForecastAreas';
import HazardForecastAreaView from '@components/HazardForecastArea/HazardForecastAreaView';
import HazardForecastAreaCreate from '@components/HazardForecastArea/HazardForecastAreaCreate';
import HazardForecastAreaEdit from '@components/HazardForecastArea/HazardForecastAreaEdit';
import HazardForecastTemplateList from '@components/HazardForecastTemplate/HazardForecastTemplateList';
import AvalanchePathsCreate from '@components/AvalanchePathsAdmin/AvalanchePathsCreate';
import AvalanchePathsView from '@components/AvalanchePathsAdmin/AvalanchePathsView';
import AvalanchePathsEdit from '@components/AvalanchePathsAdmin/AvalanchePathsEdit';
import AvalanchePathAdmin from '@components/AvalanchePathsAdmin/AvalanchePathsAdmin';

const PageNotFound = () => {
  return <p>Page not found</p>;
};

const Main = () => {
  return (
    <>
      <ErrorDialogModal></ErrorDialogModal>
      <ErrorBoundary>
        <Routes>
          <Route exact path="/" element={<Home />} />
          <Route path={Views.DataTable} element={<DataTable />} />
          <Route path={Views.IngestionFailure} element={<IngestionFailuresTable />} />
          <Route path={Views.AccessRequest} element={<AccessRequestTable />} />
          <Route path={Views.User} element={<UserAdmin />} />
          <Route path={Views.MyProfile} element={<MyProfile />} />
          <Route path={Views.UserPreferences} element={<UserPreferences />} />
          <Route path={Views.Role} element={<RoleAdmin />} />
          <Route path={Views.RoadClosureLocations} element={<RoadClosureLocationsAdmin />} />
          <Route path={Views.RoadClosureAreas} element={<RoadClosureAreasAdmin />} />
          <Route path={Views.AvalancheAreas} element={<AvalancheAreasAdmin />} />

          <Route path={Views.AvalanchePaths}>
            <Route index={true} element={<AvalanchePathAdmin />} />
            <Route path={`${FORM_MODE.CREATE}`} element={<AvalanchePathsCreate />} />
            <Route path={`${FORM_MODE.VIEW}/:id`} element={<AvalanchePathsView />} />
            <Route path={`${FORM_MODE.EDIT}/:id`} element={<AvalanchePathsEdit />} />
          </Route>

          <Route path={Views.Manufacturer} element={<ManufacturerAdmin />} />
          <Route path={Views.SensorType} element={<SensorTypeAdmin />} />
          <Route path={Views.WeatherStation} element={<WeatherStationAdmin />} />
          <Route path={Views.ObservationType} element={<ObservationTypeAdmin />} />
          <Route path={Views.SensorEquipment} element={<SensorAdmin />} />
          <Route path={Views.SensorModel} element={<SensorModelAdmin />} />
          <Route path={Views.SoilType} element={<SoilTypesAdmin />} />
          <Route path={Views.Maps} element={<Map />} />
          <Route path={Views.Dashboard} element={<DashboardPage />} />
          <Route path={Views.SingleStationGraph} element={<SingleStationGraph />} />
          <Route path={Views.MultiStationGraph} element={<MultiStationGraph />} />
          <Route path={Views.GraphPresets} element={<GraphPresets />} />
          <Route path={Views.FrostProbe} element={<FrostProbe />} />

          <Route path={Views.StandardGraphAxis} element={<StandardGraphAxis />} />
          <Route path={`${Views.StandardGraphAxis}/${FORM_MODE.CREATE}`} element={<StandardGraphAxisCreate />} />
          <Route path={`${Views.StandardGraphAxis}/${FORM_MODE.EDIT}/:id`} element={<StandardGraphAxisEdit />} />
          <Route path={`${Views.StandardGraphAxis}/${FORM_MODE.VIEW}/:id`} element={<StandardGraphAxisView />} />

          <Route path={Views.Avalanche} element={<AvalancheEventWrapper />} />
          <Route
            path={`${Views.AvalancheProcessedEvent}:id/view`}
            element={
              <AwpContainer>
                <AvalancheProcessedEvent initialMode={FORM_MODE.VIEW} initialEventType={EventTypes.HazardForecast} />
              </AwpContainer>
            }
          />
          <Route
            path={`${Views.AvalancheProcessedEvent}:id/edit`}
            element={
              <AwpContainer>
                <AvalancheProcessedEvent initialMode={FORM_MODE.EDIT} initialEventType={EventTypes.HazardForecast} />
              </AwpContainer>
            }
          />
          <Route path={Views.AvalancheProcessedEvent} element={<AvalancheProcessedEventWrapper />} />

          <Route path={Views.AvalanchePrograms} element={<AvalanchePrograms />} />
          <Route path={`${Views.AvalanchePrograms}/${FORM_MODE.CREATE}`} element={<AvalancheProgramCreate />} />
          <Route path={`${Views.AvalanchePrograms}/${FORM_MODE.EDIT}/:id`} element={<AvalancheProgramEdit />} />
          <Route path={`${Views.AvalanchePrograms}/${FORM_MODE.VIEW}/:id`} element={<AvalancheProgramView />} />

          <Route path={Views.HazardForecastArea} element={<HazardForecastAreas />} />
          <Route path={`${Views.HazardForecastArea}/${FORM_MODE.CREATE}`} element={<HazardForecastAreaCreate />} />
          <Route path={`${Views.HazardForecastArea}/${FORM_MODE.EDIT}/:id`} element={<HazardForecastAreaEdit />} />
          <Route path={`${Views.HazardForecastArea}/${FORM_MODE.VIEW}/:id`} element={<HazardForecastAreaView />} />

          <Route path={Views.HazardForecastTemplate} element={<HazardForecastTemplateList />} />

          <Route path={Views.Notification} element={<Notification />} />
          <Route path={Views.AvalancheMonitor} element={<AvalancheDashboardPage />} />
          <Route path={Views.ForecastRecipient} element={<ForecastRecipient />} />
          <Route path={Views.ManualDataValidation} element={<ManualDataValidation />} />
          <Route path={Views.Vms} element={<VariableMessageSignAdmin />} />
          <Route path={Views.VmsControl} element={<VMSControlAdmin />} />
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </ErrorBoundary>
      <ToastContainer autoClose={700} />
    </>
  );
};

export default Main;
